import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { getLandingPath } from '@/lib/auth/route-access';
import { ShieldAlert } from '@/lib/icons';

/**
 * Where the route guard sends an operator who reached a screen they may not
 * open — see the `beforeLoad` in `routes/_layout.tsx`.
 *
 * The guard used to bounce them to their landing page instead. That is
 * indistinguishable from a bug: a branch manager tapping "Enrol units" landed
 * on the dashboard with no explanation and reported the link as broken. Saying
 * plainly that the screen is administrator-only costs one page and answers the
 * question the silent redirect raised.
 *
 * This route carries no permission requirement of its own — it has to stay
 * reachable by exactly the people who are not allowed anywhere else.
 */
const searchSchema = z.object({
  /** The path that was refused, shown so the operator can name it in a request. */
  from: z.string().optional(),
});

export const Route = createFileRoute('/_layout/forbidden')({
  component: ForbiddenPage,
  validateSearch: searchSchema,
});

function ForbiddenPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { from } = Route.useSearch();
  const landing = getLandingPath(user);

  return (
    <div className="flex items-center justify-center py-16">
      <Card className="w-full max-w-lg border-0 shadow-md">
        <CardContent className="p-10 text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
            <ShieldAlert className="h-7 w-7" />
          </div>
          <h1 className="text-xl font-bold tracking-tight">
            {t('common.accessDenied')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('common.noPermissionToView')}
          </p>
          {from && (
            <p className="mt-4 rounded-lg bg-muted/60 px-3 py-2 font-mono text-xs text-muted-foreground break-all">
              {from}
            </p>
          )}
          <Button
            className="mt-6"
            onClick={() => navigate({ to: landing, replace: true })}
          >
            {t('common.backToWorkspace')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
